/**
 * Création de l'organisation du compte (ORG-4, ORG-5) : un nom seul, le domaine se rattache
 * ensuite depuis la page Organisation. Un compte ne peut en avoir qu'une — le serveur tranche
 * (`409 ALREADY_IN_ORGANIZATION`), l'écran ne fait qu'expliquer le refus.
 *
 * Écran à formulaire unique (même gabarit que `ProfilePage`) : pas de `PrimaryButton` dans
 * `PageHeader`, le bouton de soumission reste dans le formulaire (`BTN_GHOST`) — cf. règle de
 * cohérence 1.3.0 d'`@exidis/ui` (un seul primaire par écran, ici aucun n'est nécessaire).
 *
 * Mode `registre` (lot O3) : la création est un geste de gestion réservé au porteur Microsoft
 * (403 `MSAL_REQUIS` sinon). Sans configuration CIAM le bouton est désactivé et l'écran dit
 * pourquoi, plutôt que d'attendre un refus. Les refus du registre (ORG-4 avec le nom de
 * l'organisation désignée, plafond quotidien, arbitrage de reprise, registre indisponible) sont
 * traduits par `messageModeleOrganisation`.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { ciamConfigured } from '../lib/auth.js';
import { messageModeleOrganisation } from '../lib/org-errors.js';
import { useAsync } from '../hooks/useAsync.js';
import { useOrganisationStudio } from '../hooks/useOrganisationStudio.js';
import { notifyOrganizationsChanged } from '../lib/organizationsBus.js';
import { PageBody, PageHeader, Panel } from '../components/common.jsx';
import { BTN_GHOST, FORM_FIELD, FORM_LABEL, INPUT } from '../ui/theme.js';

export function CreateOrganizationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const registre = useOrganisationStudio() === 'registre';
  const sansPorteurMicrosoft = registre && !ciamConfigured;
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // I2 : une adhésion `requested`/`suspended` (non filtrée par `/me/organizations`) bloque la
  // création (ORG-4). L'écran l'explique au lieu d'offrir un formulaire voué au 409, et permet
  // de retirer une demande en attente — seule issue que le compte a seul.
  const { data: adhesionsData, reload } = useAsync(
    (signal) => api.get('/me/organizations', { signal }),
    [],
  );
  const bloquante = (adhesionsData?.data ?? []).find(
    (organization) => organization.status === 'requested' || organization.status === 'suspended',
  );

  const withdraw = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await api.delete(`/organizations/${bloquante.id}/join-requests/me`);
      notifyOrganizationsChanged();
      reload();
    } catch (failure) {
      setError(t('organization.create.withdrawFailed', { message: failure.message }));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const nom = name.trim();
    if (!nom) return;
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post('/organizations', { name: nom });
      notifyOrganizationsChanged();
      navigate(`/organisation/${data.id}`);
    } catch (failure) {
      setError(
        failure.details?.code === 'ALREADY_IN_ORGANIZATION'
          ? t('organization.create.alreadyInOrganization')
          : (messageModeleOrganisation(failure, t) ??
              t('organization.create.failed', { message: failure.message })),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (bloquante) {
    return (
      <div>
        <PageHeader title={bloquante.name} />
        <PageBody className="p-4">
          <Panel className="max-w-xl">
            <p className="text-sm text-ink-soft">
              {bloquante.status === 'requested'
                ? t('organization.create.pending', { name: bloquante.name })
                : t('organization.create.suspended', { name: bloquante.name })}
            </p>
            {error && (
              <p role="alert" className="mt-3 text-sm text-accent-ink">
                {error}
              </p>
            )}
            {bloquante.status === 'requested' && (
              <button
                type="button"
                className={`${BTN_GHOST} mt-3`}
                onClick={withdraw}
                disabled={submitting}
              >
                {t('organization.create.withdraw')}
              </button>
            )}
          </Panel>
        </PageBody>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t('organization.create.title')} />
      <PageBody className="p-4">
        <Panel className="max-w-xl">
          <p className="text-sm text-ink-soft">{t('organization.create.intro')}</p>
          <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
            <label className={FORM_FIELD}>
              <span className={FORM_LABEL}>{t('organization.create.nameLabel')}</span>
              <input
                className={INPUT}
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={120}
                required
              />
            </label>

            {sansPorteurMicrosoft && (
              <p className="text-sm text-ink-soft">{t('organization.create.msalRequis')}</p>
            )}

            {error && (
              <p role="alert" className="text-sm text-accent-ink">
                {error}
              </p>
            )}

            <button
              type="submit"
              className={BTN_GHOST}
              disabled={submitting || sansPorteurMicrosoft}
            >
              {t('organization.create.submit')}
            </button>
          </form>
        </Panel>
      </PageBody>
    </div>
  );
}
